/**
 * Charge Items master API (Day-1 IPD) — UNION module (integration merge of the
 * S1 catalog lane's full CRUD + the S2 desk lane's read-side picker). Both
 * lanes shipped a `chargeItemsApi.js`; per the desk lane's own note the merge
 * only ADDS functions (the desk file was a strict list-only subset).
 *
 * The admin-maintained catalog of billable items (name + price + GST) that the
 * S2 desk picker reads from and item-charges price against.
 *
 * Backend endpoints (org from session; behind the admission gate +
 * MANAGE_CHARGE_MASTER for writes / ANY(ADD_CHARGE, MANAGE_CHARGE_MASTER) for
 * the list — FROZEN CONTRACT, BUILD-PLAN-day1.md §2.1):
 *
 *   POST   /api/organisation/charge-items
 *   GET    /api/organisation/charge-items?active_only=
 *   PATCH  /api/organisation/charge-items/:item_id
 *   DELETE /api/organisation/charge-items/:item_id   (soft-deactivate)
 *
 * `price` travels as a plain rupee float (the master stores rupees; the
 * server converts to paise at charge-post time) — never paise here, unlike
 * the admission-charges/bill APIs. Every function returns
 * `{ success, data?, error?, code? }` — `code` carries the server's
 * machine-readable business-rule code from a `{code, message}` detail
 * (CHARGE_ITEM_NOT_FOUND, INVALID_GST_RATE, ADMISSION_DISABLED).
 *
 * Clone of surgeryApi.js's shape (org-global, no dept scoping — dept-tag is
 * DEFERRED per S1's admin lifecycle table).
 */

import apiClient from './apiClient';

const BASE = '/api/organisation/charge-items';

/**
 * GST rates the charge-items master accepts. FROZEN to 0|5|12|18 per
 * BUILD-PLAN-day1.md §2.1 ("matches existing admission_charge Literal; NOT
 * 28 per design sketch — code has no 28") — narrower than the shared
 * GST_RATES constant (`constants/bedCategory.js`) used by other B2 catalogs,
 * which DO allow 28%. Kept local/exported from here (not the shared list) so
 * this page's dropdown can't silently drift onto the wider set.
 */
export const CHARGE_ITEM_GST_RATES = [0, 5, 12, 18];

/** Local clone of the catalog error translator (see surgeryApi.js).
 * Preserves the server's `{code, message, ...extras}` business-rule detail
 * (the ...extras carry per-error fields e.g. the reversal window's
 * original_charge_date) and falls back to a friendly status message. */
function parseError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.code) {
    const { code, message, ...extras } = detail;
    return { error: message || fallback, code, ...extras };
  }
  if (typeof detail === 'string') return { error: detail };

  switch (error?.response?.status) {
    case 400: return { error: 'Invalid data — please check the form and try again.' };
    case 401: return { error: 'Session expired. Please login again.' };
    case 403: return { error: 'You do not have permission to perform this action.' };
    case 404: return { error: 'The requested item could not be found.' };
    case 409: return { error: 'That item already exists. Pick a different name.' };
    case 422: return { error: 'Invalid data — please check the form and try again.' };
    case 503: return { error: 'Charge items are temporarily unavailable. Try again shortly.', code: 'IPD_TABLE_UNAVAILABLE' };
    case 500: return { error: 'Server error. Please try again in a moment.' };
    default:  return { error: fallback };
  }
}

/** POST /api/organisation/charge-items → 201 ChargeItem */
export const createChargeItem = async (body) => {
  try {
    const response = await apiClient.post(BASE, body);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[ChargeItemsAPI] create error:', error);
    return { success: false, ...parseError(error, 'Failed to create charge item.') };
  }
};

/**
 * GET /api/organisation/charge-items?active_only= → {items:[...], count}
 * Accepts BOTH call styles from the two merged lanes: the catalog store passes
 * `{ active_only }` (snake, an explicit bool), the desk picker passes
 * `{ activeOnly }` (camel). Default (no arg) → active_only=true (active items
 * only). Pass either flag `false` to include deactivated items.
 */
export const listChargeItems = async ({ active_only, activeOnly } = {}) => {
  try {
    const resolved = active_only ?? activeOnly ?? true;
    const response = await apiClient.get(BASE, { params: { active_only: resolved } });
    return {
      success: true,
      data: {
        items: response.data?.items || [],
        count: response.data?.count ?? (response.data?.items?.length || 0),
      },
    };
  } catch (error) {
    console.error('[ChargeItemsAPI] list error:', error);
    return { success: false, ...parseError(error, 'Failed to load charge items.') };
  }
};

/** PATCH /api/organisation/charge-items/:id — partial update (name/price/gst_rate/category/active). */
export const updateChargeItem = async (item_id, body) => {
  try {
    const response = await apiClient.patch(`${BASE}/${encodeURIComponent(item_id)}`, body);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[ChargeItemsAPI] update error:', error);
    return { success: false, ...parseError(error, 'Failed to update charge item.') };
  }
};

/** DELETE /api/organisation/charge-items/:id → 200 {item_id, active:false} (soft-deactivate). */
export const deactivateChargeItem = async (item_id) => {
  try {
    const response = await apiClient.delete(`${BASE}/${encodeURIComponent(item_id)}`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[ChargeItemsAPI] deactivate error:', error);
    return { success: false, ...parseError(error, 'Failed to deactivate charge item.') };
  }
};

const chargeItemsApi = {
  listChargeItems,
  createChargeItem,
  updateChargeItem,
  deactivateChargeItem,
  CHARGE_ITEM_GST_RATES,
};

export default chargeItemsApi;
